export type ListingPage<Card> = {
  page: number; pages: number; total: number;
  cards: Array<{ reference: string; card: Card }>;
  next: string | null;
};

export interface InfiniteListingController {
  readonly lastLoadedPage: number;
  ensureReference(reference: string, lastLoadedPage: number): Promise<boolean>;
  setPaused(paused: boolean): void;
}

const filterNames = new Set(['kind', 'estate', 'development', 'propertyType', 'minPrice', 'maxPrice', 'bedrooms', 'bathrooms', 'status', 'onShow', 'feature', 'sort']);

// Pagination can add default kind/sort values and number repeated feature keys.
export function searchSignature(url: URL): string {
  const values: Array<[string, string]> = [];
  for (const [rawName, rawValue] of url.searchParams) {
    const name = rawName.replace(/\[(?:\d*)\]$/, '');
    if (!filterNames.has(name) || !rawValue.trim()) continue;
    let value = rawValue.trim();
    if (name === 'kind' && value === 'sale') value = 'buy';
    if ((name === 'kind' && value === 'all') || (name === 'sort' && value === 'featured')) continue;
    if (['minPrice', 'maxPrice', 'bedrooms', 'bathrooms'].includes(name)) value = String(Math.max(0, Number.parseInt(value, 10) || 0));
    if (name === 'onShow') {
      if (!['1', 'true', 'yes', 'on'].includes(value.toLowerCase())) continue;
      value = '1';
    }
    values.push([name, value]);
  }
  return JSON.stringify([...new Set(values.map(value => JSON.stringify(value)))].sort());
}

export function nextResultsUrl(raw: string, base: URL, expectedPage: number): string {
  const target = new URL(raw, base);
  if (target.origin !== base.origin || !/^\/properties\/?$/.test(target.pathname)
    || target.username || target.password || !['http:', 'https:'].includes(target.protocol)
    || target.searchParams.getAll('page').length !== 1 || target.searchParams.get('page') !== String(expectedPage)
    || searchSignature(target) !== searchSignature(base)) throw new Error('Unexpected results page URL');
  target.hash = '';
  return target.href;
}

export function validPage<Card>(page: ListingPage<Card>, expectedPage: number): void {
  if (!Number.isInteger(page.page) || page.page < 1 || page.page !== expectedPage || !Number.isInteger(page.pages) || page.pages < page.page || page.pages > 10000
    || !Number.isInteger(page.total) || page.total < 0 || page.cards.length > 24
    || page.cards.some(item => !item.reference.trim()) || (page.page < page.pages && !page.next)
    || (page.page >= page.pages && page.next)) throw new Error('Unexpected results page content');
}

/** Request/state logic is independent of DOM so retries and restoration are testable. */
export class ListingPager<Card> {
  page: number;
  pages: number;
  total: number;
  next: string | null;
  busy = false;
  failed = false;
  readonly references: Set<string>;
  private inFlight: Promise<boolean> | null = null;

  constructor(initial: ListingPage<Card>, private readonly base: URL,
    private readonly fetchPage: (url: string, page: number) => Promise<ListingPage<Card>>,
    private readonly append: (cards: Array<{ reference: string; card: Card }>) => void,
    private readonly changed: () => void = () => {},
    private readonly reportFailure: (error: unknown, page: number) => void = () => {}) {
    validPage(initial, initial.page);
    this.page = initial.page; this.pages = initial.pages; this.total = initial.total;
    this.next = initial.next ? nextResultsUrl(initial.next, base, initial.page + 1) : null;
    this.references = new Set(initial.cards.map(item => item.reference));
  }

  loadNext(retry = false): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    if (!this.next || (this.failed && !retry)) return Promise.resolve(false);
    const url = this.next;
    const expectedPage = this.page + 1;
    this.busy = true; this.failed = false;
    this.changed();
    this.inFlight = (async () => {
      try {
        const page = await this.fetchPage(url, expectedPage);
        validPage(page, expectedPage);
        const next = page.next ? nextResultsUrl(page.next, this.base, expectedPage + 1) : null;
        const newReferences = new Set<string>();
        const cards = page.cards.filter(item => {
          if (this.references.has(item.reference) || newReferences.has(item.reference)) return false;
          newReferences.add(item.reference); return true;
        });
        this.append(cards);
        newReferences.forEach(reference => this.references.add(reference));
        this.page = page.page; this.pages = page.pages; this.total = page.total; this.next = next;
        return true;
      } catch (error) {
        this.failed = true;
        this.reportFailure(error, expectedPage);
        return false;
      } finally {
        this.busy = false; this.inFlight = null; this.changed();
      }
    })();
    return this.inFlight;
  }

  async ensureReference(reference: string, lastPage: number): Promise<boolean> {
    if (!Number.isInteger(lastPage) || lastPage < this.page || lastPage > 10000) return this.references.has(reference);
    while (this.page < Math.min(lastPage, this.pages) && this.next) {
      if (!await this.loadNext()) return false;
    }
    return this.references.has(reference);
  }
}

function integer(value: string | undefined, minimum: number): number {
  if (!value || !/^\d+$/.test(value)) throw new Error('Missing results metadata');
  const result = Number(value);
  if (!Number.isSafeInteger(result) || result < minimum) throw new Error('Invalid results metadata');
  return result;
}

export function readResultsPage(document: Document, expectedPage?: number): ListingPage<HTMLElement> {
  const wrapper = document.querySelector<HTMLElement>('.nce-property-search-page [data-nce-infinite-results]');
  const grid = wrapper?.querySelector<HTMLElement>('[data-listing-results]');
  if (!wrapper || !grid) throw new Error('Results markup missing');
  const page: ListingPage<HTMLElement> = {
    page: integer(wrapper.dataset.resultsPage, 1), pages: integer(wrapper.dataset.resultsPages, 1),
    total: integer(wrapper.dataset.resultsTotal, 0),
    cards: Array.from(grid.querySelectorAll<HTMLElement>(':scope > .nce-listing-card[data-property-reference]'))
      .map(card => ({ reference: card.dataset.propertyReference || '', card })),
    next: wrapper.querySelector<HTMLAnchorElement>('.nce-pagination a.next[href]')?.getAttribute('href') || null,
  };
  validPage(page, expectedPage ?? page.page);
  return page;
}

export function validateResponse(response: Pick<Response, 'ok' | 'redirected' | 'url' | 'headers'>, requestedUrl: string): void {
  if (!response.ok || response.redirected || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Results response unavailable');
  const actual = new URL(response.url);
  const expected = new URL(requestedUrl);
  actual.hash = ''; expected.hash = '';
  actual.searchParams.sort(); expected.searchParams.sort();
  if (actual.href !== expected.href) throw new Error('Unexpected results response URL');
}

export class ResultsLoadError extends Error {
  constructor(readonly code: 'network' | 'temporary-http' | 'invalid-response' | 'invalid-results', readonly attempt: number, readonly status?: number) {
    super(code);
    this.name = 'ResultsLoadError';
  }
}

export interface ResultsRequestOptions {
  fetcher?: typeof fetch;
  wait?: (milliseconds: number) => Promise<void>;
  timeoutMs?: number;
}

/** A batch remains one in-flight operation, including its bounded network retries. */
export async function requestResultsPage<Card>(url: string, expectedPage: number,
  parse: (html: string, expectedPage: number) => ListingPage<Card>, options: ResultsRequestOptions = {}): Promise<ListingPage<Card>> {
  const fetcher = options.fetcher ?? fetch;
  const wait = options.wait ?? (milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds)));
  const transientStatuses = new Set([408, 429, 500, 502, 503, 504]);
  for (let attempt = 1; attempt <= 3; attempt++) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), options.timeoutMs ?? 8000);
    let retryDelay = attempt === 1 ? 500 : 1500;
    let retryAllowed = true;
    try {
      const response = await fetcher(url, {
        credentials: 'same-origin', redirect: 'error', cache: 'no-store',
        signal: abort.signal, headers: { Accept: 'text/html' },
      });
      if (transientStatuses.has(response.status)) {
        // Respect a short Retry-After; do not hammer a service asking for a long pause.
        const retryAfter = response.headers.get('retry-after');
        if (retryAfter) {
          const seconds = Number(retryAfter);
          const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now();
          if (Number.isFinite(delay)) {
            retryAllowed = delay <= 5000;
            retryDelay = Math.max(retryDelay, Math.min(5000, delay));
          }
        }
        throw new ResultsLoadError('temporary-http', attempt, response.status);
      }
      try { validateResponse(response, url); }
      catch { throw new ResultsLoadError('invalid-response', attempt, response.status); }
      const html = await response.text();
      try { return parse(html, expectedPage); }
      catch { throw new ResultsLoadError('invalid-results', attempt, response.status); }
    } catch (error) {
      const failure = error instanceof ResultsLoadError ? error : new ResultsLoadError('network', attempt);
      if (attempt === 3 || !retryAllowed || !['network', 'temporary-http'].includes(failure.code)) throw failure;
    } finally { clearTimeout(timeout); }
    await wait(retryDelay);
  }
  throw new ResultsLoadError('network', 3);
}

export function initInfiniteListings(onAppend: () => void = () => {}): InfiniteListingController | null {
  if (!/^\/properties\/?$/.test(window.location.pathname) || !('fetch' in window) || !('DOMParser' in window)) return null;
  const wrapper = document.querySelector<HTMLElement>('.nce-property-search-page [data-nce-infinite-results]');
  const grid = wrapper?.querySelector<HTMLElement>('[data-listing-results]');
  const pagination = wrapper?.querySelector<HTMLElement>('.nce-pagination');
  if (!wrapper || !grid || wrapper.hasAttribute('data-infinite-enhanced')) return null;
  let initial: ListingPage<HTMLElement>;
  try { initial = readResultsPage(document); } catch { return null; }
  const controls = document.createElement('div');
  controls.className = 'nce-infinite-results nce-infinite-results__controls';
  const status = document.createElement('p');
  status.dataset.infiniteStatus = ''; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true');
  const button = document.createElement('button');
  button.type = 'button'; button.className = 'nce-button nce-infinite-results__more'; button.dataset.infiniteLoad = '';
  const fallback = document.createElement('a');
  fallback.className = 'nce-text-link'; fallback.textContent = 'Open next page'; fallback.hidden = true;
  const sentinel = document.createElement('div');
  sentinel.dataset.infiniteSentinel = ''; sentinel.setAttribute('aria-hidden', 'true');
  controls.append(status, button, fallback, sentinel);
  let paused = false;
  let autoFrame = 0;
  let observer: IntersectionObserver | null = null;
  let pager: ListingPager<HTMLElement>;
  const autoLoad = () => {
    autoFrame = 0;
    if (paused || pager.busy || pager.failed || !pager.next || !observer || document.hidden) return;
    if (sentinel.getBoundingClientRect().top < window.innerHeight + 1200) void pager.loadNext();
  };
  const scheduleAuto = () => {
    if (!autoFrame) autoFrame = window.requestAnimationFrame(autoLoad);
  };
  const render = () => {
    const count = pager.references.size;
    const summary = `${count}${pager.total >= count ? ` of ${pager.total}` : ''} properties shown.`;
    status.textContent = pager.failed ? `${summary} More properties could not be loaded. Please try again.`
      : pager.busy ? `${summary} Loading more properties…`
      : pager.next ? summary : `${summary} You’ve reached the end of these results.`;
    button.textContent = pager.failed ? 'Try again' : pager.busy ? 'Loading…' : pager.next ? 'Load more properties' : 'End of results';
    // aria-disabled plus an activation guard preserves keyboard focus while loading.
    button.hidden = ((!pager.next || (Boolean(observer) && !pager.failed)) && document.activeElement !== button);
    button.setAttribute('aria-disabled', String(!pager.next || pager.busy));
    grid.setAttribute('aria-busy', String(pager.busy));
    fallback.hidden = !pager.failed || !pager.next;
    if (pager.next) fallback.href = `${pager.next}#property-results`;
    wrapper.dataset.resultsPage = String(pager.page);
    if (!pager.busy && !pager.failed) scheduleAuto();
  };
  const fetchPage = async (url: string, expectedPage: number): Promise<ListingPage<HTMLElement>> => {
    return requestResultsPage(url, expectedPage,
      (html, page) => readResultsPage(new DOMParser().parseFromString(html, 'text/html'), page));
  };
  try {
    pager = new ListingPager(initial, new URL(window.location.href), fetchPage, cards => {
      const fragment = document.createDocumentFragment();
      cards.forEach(({ card }) => fragment.append(document.importNode(card, true)));
      grid.append(fragment);
      onAppend();
      window.dispatchEvent(new CustomEvent('nce:results-appended', { detail: { count: cards.length } }));
    }, render, (error, page) => {
      // No URLs, filter values, HTML or customer details in diagnostics.
      const failure = error instanceof ResultsLoadError ? error : null;
      console.warn('[NCE property results]', { page, code: failure?.code ?? 'invalid-page', attempt: failure?.attempt, status: failure?.status });
    });
  } catch { return null; }
  grid.after(controls);
  wrapper.setAttribute('data-infinite-enhanced', '');
  // Appending above a visible footer must not anchor the viewport to that footer:
  // doing so jumps past the new cards and can drain every batch without a scroll.
  document.documentElement.style.overflowAnchor = 'none';
  if (pagination) pagination.hidden = true;
  button.addEventListener('click', () => { if (!paused && !pager.busy && pager.next) void pager.loadNext(true); });
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) scheduleAuto();
    }, { rootMargin: '1200px 0px', threshold: 0 });
    observer.observe(sentinel);
  }
  render();
  return {
    get lastLoadedPage() { return pager.page; },
    ensureReference: (reference, lastLoadedPage) => pager.ensureReference(reference, lastLoadedPage),
    setPaused(value) { paused = value; if (!paused) scheduleAuto(); },
  };
}
